import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase.js";
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  query,
  orderBy,
  getDoc,
  updateDoc,
  where,
  addDoc,
  serverTimestamp
} from "firebase/firestore";
import { checkIsAdmin } from "../utils/adminUtils.js";
import { getUserProfile } from "../utils/userUtils.js";
import { setUserRole, assignStudentToCoach, removeStudentFromCoach } from "../utils/coachUtils";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { getFunctions, httpsCallable } from "firebase/functions"; // Firebase Functions için eklendi

function AdminPanel() {
  const [isChecking, setIsChecking] = useState(true);
  const [allowed, setAllowed] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [forumPosts, setForumPosts] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [users, setUsers] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [noReferralUsers, setNoReferralUsers] = useState([]);
  const [stats, setStats] = useState({ users: 0, posts: 0, feedbacks: 0 });
  const [activeTab, setActiveTab] = useState("overview");
  const [searchQueries, setSearchQueries] = useState({
    forum: "",
    feedback: "",
    users: "",
    referrals: "",
  });
  const [displayCounts, setDisplayCounts] = useState({
    forum: 10,
    feedback: 10,
    users: 10,
    referrals: 10,
  });
  const [userSearch, setUserSearch] = useState("");
  const [searchedUser, setSearchedUser] = useState(null);
  const [error, setError] = useState("");
  const [expandedReferral, setExpandedReferral] = useState(null);
  const [coachAssignStudentId, setCoachAssignStudentId] = useState("");
  const [coachAssignCoachId, setCoachAssignCoachId] = useState("");
  const [broadcastMessage, setBroadcastMessage] = useState("");
  const [broadcastTitle, setBroadcastTitle] = useState("");
  const [isBroadcasting, setIsBroadcasting] = useState(false);
  const navigate = useNavigate();
  const userId = localStorage.getItem("userId");

  useEffect(() => {
    const verify = async () => {
      try {
        if (!userId) {
          setError("Kullanıcı ID'si bulunamadı. Lütfen giriş yapın.");
          toast.error("Kullanıcı ID'si bulunamadı. Lütfen giriş yapın.");
          navigate("/auth");
          return;
        }

        const isAdmin = await checkIsAdmin(userId);
        if (!isAdmin) {
          setAccessDenied(true);
          setIsChecking(false);
        } else {
          setAllowed(true);
          setIsChecking(false);
          fetchStats();
          fetchForumPosts();
          fetchFeedbacks();
          fetchUsers();
          fetchReferrals();
        }
      } catch (err) {
        const errorMsg = `Admin yetkisi kontrol edilirken hata oluştu: ${err.message}`;
        setAccessDenied(true);
        setIsChecking(false);
        toast.error(errorMsg);
      }
    };
    verify();
  }, [userId, navigate]);

  const fetchStats = async () => {
    try {
      const userSnap = await getDocs(collection(db, "users"));
      const postSnap = await getDocs(collection(db, "forumPosts"));
      const feedbackSnap = await getDocs(collection(db, "feedbacks"));
      setStats({
        users: userSnap.size,
        posts: postSnap.size,
        feedbacks: feedbackSnap.size,
      });
    } catch (err) {
      const errorMsg = `İstatistikler yüklenirken hata oluştu: ${err.message}`;
      setError(errorMsg);
      toast.error(errorMsg);
    }
  };

  const fetchForumPosts = async () => {
    try {
      const q = query(collection(db, "forumPosts"), orderBy("timestamp", "desc"));
      const snapshot = await getDocs(q);
      const data = await Promise.all(
        snapshot.docs.map(async (d) => {
          const commentsSnap = await getDocs(collection(d.ref, "comments"));
          const comments = commentsSnap.docs.map((c) => ({ id: c.id, ...c.data() }));
          return { id: d.id, ...d.data(), comments };
        })
      );
      setForumPosts(data);
    } catch (err) {
      const errorMsg = `Forum postları yüklenirken hata oluştu: ${err.message}`;
      setError(errorMsg);
      toast.error(errorMsg);
    }
  };

  const fetchFeedbacks = async () => {
    try {
      const q = query(collection(db, "feedbacks"), orderBy("timestamp", "desc"));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setFeedbacks(data);
    } catch (err) {
      const errorMsg = `Geri bildirimler yüklenirken hata oluştu: ${err.message}`;
      setError(errorMsg);
      toast.error(errorMsg);
    }
  };

  const fetchUsers = async () => {
    try {
      const snapshot = await getDocs(collection(db, "users"));
      const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setUsers(data);
    } catch (err) {
      const errorMsg = `Kullanıcılar yüklenirken hata oluştu: ${err.message}`;
      setError(errorMsg);
      toast.error(errorMsg);
    }
  };

  const fetchReferrals = async () => {
    try {
      const snapshot = await getDocs(collection(db, "users"));
      const allUsers = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

      const referralData = allUsers.filter((user) => user.referralCodeUsed);
      const noReferralData = allUsers.filter((user) => !user.referralCodeUsed);

      const groupedByReferralCode = {};
      referralData.forEach((user) => {
        const code = user.referralCodeUsed;
        if (!groupedByReferralCode[code]) {
          groupedByReferralCode[code] = [];
        }
        groupedByReferralCode[code].push({
          username: user.username || "Bilinmeyen Kullanıcı",
          email: user.email || "E-posta Yok",
          isPremium: user.isPremium || false,
        });
      });

      const referralList = Object.keys(groupedByReferralCode).map((code) => ({
        referralCode: code,
        users: groupedByReferralCode[code],
      }));

      setReferrals(referralList);
      setNoReferralUsers(
        noReferralData.map((user) => ({
          username: user.username || "Bilinmeyen Kullanıcı",
          email: user.email || "E-posta Yok",
          isPremium: user.isPremium || false,
        }))
      );
    } catch (err) {
      const errorMsg = `Referans kodları yüklenirken hata oluştu: ${err.message}`;
      setError(errorMsg);
      toast.error(errorMsg);
    }
  };

  const handleDeletePost = async (postId) => {
    try {
      await deleteDoc(doc(db, "forumPosts", postId));
      toast.success("Post başarıyla silindi!");
      await fetchForumPosts();
    } catch (err) {
      const errorMsg = `Post silinirken hata oluştu: ${err.message}`;
      setError(errorMsg);
      toast.error(errorMsg);
    }
  };

  const handleDeleteComment = async (postId, commentId) => {
    try {
      await deleteDoc(doc(db, "forumPosts", postId, "comments", commentId));
      toast.success("Yorum başarıyla silindi!");
      await fetchForumPosts();
    } catch (err) {
      const errorMsg = `Yorum silinirken hata oluştu: ${err.message}`;
      setError(errorMsg);
      toast.error(errorMsg);
    }
  };

  const handleDeleteFeedback = async (id) => {
    try {
      await deleteDoc(doc(db, "feedbacks", id));
      toast.success("Geri bildirim başarıyla silindi!");
      await fetchFeedbacks();
    } catch (err) {
      const errorMsg = `Geri bildirim silinirken hata oluştu: ${err.message}`;
      setError(errorMsg);
      toast.error(errorMsg);
    }
  };

  const handleStatusChange = async (feedbackId, newStatus) => {
    try {
      const feedbackRef = doc(db, "feedbacks", feedbackId);
      await updateDoc(feedbackRef, { status: newStatus });
      setFeedbacks((prev) =>
        prev.map((fb) => (fb.id === feedbackId ? { ...fb, status: newStatus } : fb))
      );
      toast.success("Geri bildirim durumu güncellendi!");
    } catch (err) {
      const errorMsg = `Durum güncellenirken hata oluştu: ${err.message}`;
      setError(errorMsg);
      toast.error(errorMsg);
    }
  };

  const handleBanUser = async (userId) => {
    try {
      // Kullanıcıya ait postları sil
      const postsQuery = query(collection(db, "forumPosts"), where("userId", "==", userId));
      const postsSnapshot = await getDocs(postsQuery);
      for (const postDoc of postsSnapshot.docs) {
        await deleteDoc(doc(db, "forumPosts", postDoc.id));
      }

      // Kullanıcıyı sil
      await deleteDoc(doc(db, "users", userId));

      toast.success("Kullanıcı başarıyla banlandı ve verileri silindi!");
      // Verileri yenile
      await fetchUsers();
      await fetchReferrals();
      await fetchForumPosts();
    } catch (err) {
      const errorMsg = `Kullanıcı banlanırken hata oluştu: ${err.message}`;
      setError(errorMsg);
      toast.error(errorMsg);
    }
  };

  const handleUserSearch = () => {
    const allUsers = [
      ...referrals.flatMap((r) => r.users.map((u) => ({ ...u, referralCode: r.referralCode }))),
      ...noReferralUsers.map((u) => ({ ...u, referralCode: null })),
    ];

    const foundUser = allUsers.find((u) => {
      const username = u.username || "";
      const email = u.email || "";
      return (
        username.toLowerCase().includes(userSearch.toLowerCase()) ||
        email.toLowerCase().includes(userSearch.toLowerCase())
      );
    });

    if (foundUser) {
      setSearchedUser(foundUser);
      if (foundUser.referralCode) {
        setExpandedReferral(foundUser.referralCode);
      } else {
        setExpandedReferral("no-referral");
      }
      toast.success("Kullanıcı bulundu!");
    } else {
      setSearchedUser(null);
      setError("Kullanıcı bulunamadı.");
      toast.error("Kullanıcı bulunamadı.");
    }
  };

  // Liderlik tablosunu sıfırlama fonksiyonu
  const handleResetLeaderboard = async (period) => {
    try {
      const functions = getFunctions();
      const resetLeaderboardManual = httpsCallable(functions, "resetLeaderboardManual");
      const result = await resetLeaderboardManual({ period });
      toast.success(result.data.message);
    } catch (err) {
      const errorMsg = `${period} liderlik tablosu sıfırlanırken hata: ${err.message}`;
      setError(errorMsg);
      toast.error(errorMsg);
    }
  };

  const handleSendBroadcast = async () => {
    if (!broadcastTitle.trim() || !broadcastMessage.trim()) {
      toast.error("Başlık ve mesaj alanları boş bırakılamaz!");
      return;
    }
    
    setIsBroadcasting(true);
    const toastId = toast.loading("📢 Tüm kullanıcılara bildirim gönderiliyor...");
    
    try {
      let count = 0;
      for (const u of users) {
        if (!u.id) continue;
        await addDoc(collection(db, "notifications"), {
          userId: u.id,
          title: broadcastTitle,
          message: broadcastMessage,
          icon: "📢",
          type: "system",
          timestamp: serverTimestamp(),
          read: false
        });
        count++;
      }
      toast.success(`${count} kullanıcıya sistem bildirimi başarıyla gönderildi!`, { id: toastId });
      setBroadcastTitle("");
      setBroadcastMessage("");
    } catch (err) {
      console.error(err);
      toast.error("Bildirim gönderilirken bir hata oluştu.", { id: toastId });
    } finally {
      setIsBroadcasting(false);
    }
  };

  const togglePremium = async (targetUserId, currentStatus) => {
    try {
      const userRef = doc(db, "users", targetUserId);
      await updateDoc(userRef, { isPremium: !currentStatus });
      toast.success(`Kullanıcı başarıyla ${!currentStatus ? 'Premium yapıldı! ✨' : 'Standart üyeye çevrildi.'}`);
      fetchUsers();
    } catch (err) {
      toast.error(`Premium güncellenemedi: ${err.message}`);
    }
  };

  const handleSearchChange = (tab, value) => {
    setSearchQueries((prev) => ({ ...prev, [tab]: value }));
  };

  const handleLoadMore = (tab) => {
    setDisplayCounts((prev) => ({ ...prev, [tab]: prev[tab] + 10 }));
  };

  const filteredFeedbacks = feedbacks.filter(
    (f) =>
      (f.title || "").toLowerCase().includes(searchQueries.feedback.toLowerCase()) ||
      (f.username || "").toLowerCase().includes(searchQueries.feedback.toLowerCase()) ||
      (f.description || "").toLowerCase().includes(searchQueries.feedback.toLowerCase())
  );

  const filteredPosts = forumPosts.filter(
    (p) =>
      (p.text || "").toLowerCase().includes(searchQueries.forum.toLowerCase()) ||
      (p.username || "").toLowerCase().includes(searchQueries.forum.toLowerCase())
  );

  const filteredUsers = users.filter(
    (u) =>
      (u.username || "").toLowerCase().includes(searchQueries.users.toLowerCase()) ||
      (u.email || "").toLowerCase().includes(searchQueries.users.toLowerCase()) ||
      (u.id || "").toLowerCase().includes(searchQueries.users.toLowerCase())
  );

  const resolveUserId = (inputStr) => {
    if (!inputStr) return '';
    const cleanStr = inputStr.trim();
    const byId = users.find(u => u.id === cleanStr);
    if (byId) return byId.id;
    const byName = users.find(u => u.username?.toLowerCase() === cleanStr.toLowerCase());
    if (byName) return byName.id;
    const byEmail = users.find(u => u.email?.toLowerCase() === cleanStr.toLowerCase());
    if (byEmail) return byEmail.id;
    return cleanStr;
  };

  const filteredReferrals = referrals.filter(
    (r) =>
      (r.referralCode || "").toLowerCase().includes(searchQueries.referrals.toLowerCase()) ||
      r.users.some(
        (u) =>
          (u.username || "").toLowerCase().includes(searchQueries.referrals.toLowerCase()) ||
          (u.email || "").toLowerCase().includes(searchQueries.referrals.toLowerCase())
      )
  );

  const filteredNoReferralUsers = noReferralUsers.filter(
    (u) =>
      (u.username || "").toLowerCase().includes(searchQueries.referrals.toLowerCase()) ||
      (u.email || "").toLowerCase().includes(searchQueries.referrals.toLowerCase())
  );

  if (isChecking) {
    return (
      <div style={styles.fullscreenLoading}>
        <motion.div 
          animate={{ opacity: [0.5, 1, 0.5] }} 
          transition={{ repeat: Infinity, duration: 1.5 }}
          style={styles.glitchText}
        >
          YETKİLENDİRME KONTROL EDİLİYOR...
        </motion.div>
      </div>
    );
  }

  if (accessDenied || !allowed) {
    return (
      <div style={styles.fullscreenDenied}>
        <motion.div 
          initial={{ scale: 0.8, opacity: 0 }} 
          animate={{ scale: 1, opacity: 1 }} 
          transition={{ type: 'spring', bounce: 0.5 }}
          style={{ textAlign: 'center' }}
        >
          <h1 style={styles.deniedTitle}>ACCESS DENIED</h1>
          <p style={styles.deniedSub}>Bu bölgeye erişim yetkiniz bulunmamaktadır.</p>
          <button style={styles.glitchButton} onClick={() => navigate('/home')}>Sisteme Geri Dön</button>
        </motion.div>
      </div>
    );
  }

  return (
    <div style={styles.layoutWrapper}>
      {/* Sidebar */}
      <motion.aside
        style={styles.sidebar}
        initial={{ x: -50, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div style={styles.sidebarHeader}>
          <h2>🎛️ Admin Panel</h2>
        </div>
        <nav style={styles.sidebarNav}>
          {[
            { key: "overview", label: "📊 Genel Bakış" },
            { key: "users", label: "👥 Kullanıcılar" },
            { key: "referrals", label: "🤝 Referans Kodları" },
            { key: "coaching", label: "🎓 Koç Yönetimi" },
            { key: "forum", label: "🗣️ Forum Postları" },
            { key: "feedback", label: "📩 Geri Bildirimler" },
            { key: "broadcast", label: "📢 Duyuru Gönder" },
          ].map((tab) => (
            <button
              key={tab.key}
              style={activeTab === tab.key ? styles.sidebarItemActive : styles.sidebarItem}
              onClick={() => setActiveTab(tab.key)}
              aria-label={`${tab.label} sekmesine geç`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
        <div style={{ marginTop: "auto", padding: "1rem" }}>
          <button style={styles.sidebarBackButton} onClick={() => navigate('/home')}>
            ⬅ Sisteme Dön
          </button>
        </div>
      </motion.aside>

      {/* Main Content */}
      <div style={styles.mainContent}>
        <motion.main
          style={styles.contentArea}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Top Bar showing active tab */}
          <header style={styles.topBar}>
            <h1 style={styles.topBarTitle}>
              {activeTab === "overview" && "Genel Bakış"}
              {activeTab === "users" && "Kullanıcı Yönetimi"}
              {activeTab === "referrals" && "Referans Kodları"}
              {activeTab === "coaching" && "Koçluk ve Eşleştirme"}
              {activeTab === "forum" && "Forum Denetimi"}
              {activeTab === "feedback" && "Geri Bildirimler"}
              {activeTab === "broadcast" && "Global Duyuru Sistemi"}
            </h1>
          </header>

          <AnimatePresence>
            {error && (
              <motion.p
                style={styles.errorMessage}
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
              >
                {error}
              </motion.p>
            )}
          </AnimatePresence>

          {activeTab === "overview" && (
            <div style={styles.tabContent}>
              <div style={styles.statsSection}>
                {[
                  { label: "👥 Kullanıcı Sayısı", value: stats.users },
                  { label: "📝 Forum Post Sayısı", value: stats.posts },
                  { label: "📩 Geri Bildirim Sayısı", value: stats.feedbacks },
                ].map((stat, index) => (
                  <motion.div
                    key={index}
                    style={styles.statCard}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1, duration: 0.3 }}
                  >
                    <h3>{stat.label}</h3>
                    <p style={styles.statValue}>{stat.value}</p>
                  </motion.div>
                ))}
              </div>
              
              <div style={styles.card}>
                <h3 style={{ marginBottom: '1rem', color: '#f8fafc' }}>🏆 Liderlik Tablosu Kontrolü</h3>
                <p style={{ color: '#94a3b8', fontSize: '0.95rem', marginBottom: '1.5rem' }}>
                  Liderlik tablolarını manuel olarak buradan sıfırlayabilirsiniz. 
                  (Sistem, puanları sıfırlar ve liderlik ligini haftalık veya aylık olarak günceller.)
                </p>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <motion.button
                    style={styles.actionButton}
                    onClick={() => handleResetLeaderboard("weekly")}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    🏆 Haftalık Tabloyu Sıfırla
                  </motion.button>
                  <motion.button
                    style={styles.actionButton}
                    onClick={() => handleResetLeaderboard("monthly")}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    🌟 Aylık Tabloyu Sıfırla
                  </motion.button>
                </div>
              </div>
            </div>
          )}


        {activeTab === "forum" && (
          <div style={styles.tabContent}>
            <motion.input
              type="text"
              placeholder="Post veya kullanıcı adına göre ara..."
              value={searchQueries.forum}
              onChange={(e) => handleSearchChange("forum", e.target.value)}
              style={styles.searchInput}
              whileFocus={{ borderColor: "#00c4b4" }}
              aria-label="Forum postlarını ara"
            />
            <div style={styles.cardGrid}>
              {filteredPosts.length === 0 ? (
                <p style={styles.emptyMessage}>Henüz forum postu yok.</p>
              ) : (
                filteredPosts.slice(0, displayCounts.forum).map((post, index) => (
                  <motion.div
                    key={post.id}
                    style={styles.card}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05, duration: 0.3 }}
                    whileHover={{ scale: 1.02 }}
                  >
                    <p>
                      <strong>{post.username || "👤"}</strong>: {post.text}
                    </p>
                    <p style={styles.time}>
                      {post.timestamp?.toDate?.().toLocaleString() || post.timestamp}
                    </p>
                    <motion.button
                      style={styles.actionButton}
                      onClick={() => handleDeletePost(post.id)}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      aria-label="Postu sil"
                    >
                      Postu Sil
                    </motion.button>
                    {post.comments.length > 0 && (
                      <div style={styles.commentsSection}>
                        <strong>💬 Yorumlar:</strong>
                        {post.comments.map((c, i) => (
                          <motion.div
                            key={c.id}
                            style={styles.commentCard}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.05, duration: 0.2 }}
                          >
                            <span>
                              <strong>{c.username || "👤"}</strong>: {c.text}
                            </span>
                            <span style={styles.commentTime}>
                              {c.timestamp?.toDate?.().toLocaleString() || c.timestamp}
                            </span>
                            <motion.button
                              style={styles.smallActionButton}
                              onClick={() => handleDeleteComment(post.id, c.id)}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              aria-label="Yorumu sil"
                            >
                              Sil
                            </motion.button>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </motion.div>
                ))
              )}
            </div>
            {filteredPosts.length > displayCounts.forum && (
              <motion.button
                style={styles.loadMoreButton}
                onClick={() => handleLoadMore("forum")}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                aria-label="Daha fazla forum postu göster"
              >
                Daha Fazla Göster
              </motion.button>
            )}
          </div>
        )}

        {activeTab === "feedback" && (
          <div style={styles.tabContent}>
            <motion.input
              type="text"
              placeholder="Başlık, kullanıcı veya açıklamaya göre ara..."
              value={searchQueries.feedback}
              onChange={(e) => handleSearchChange("feedback", e.target.value)}
              style={styles.searchInput}
              whileFocus={{ borderColor: "#00c4b4" }}
              aria-label="Geri bildirimleri ara"
            />
            <div style={styles.cardGrid}>
              {filteredFeedbacks.length === 0 ? (
                <p style={styles.emptyMessage}>Henüz geri bildirim yok.</p>
              ) : (
                filteredFeedbacks.slice(0, displayCounts.feedback).map((f, index) => (
                  <motion.div
                    key={f.id}
                    style={styles.card}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05, duration: 0.3 }}
                    whileHover={{ scale: 1.02 }}
                  >
                    <div style={styles.feedbackHeader}>
                      <p style={styles.feedbackTitle}>
                        <strong>{f.username || "👤"}</strong>: {f.title}
                      </p>
                      <span style={styles.feedbackType}>
                        Tür: {f.type === "suggestion" ? "Öneri" : f.type === "bug" ? "Hata" : "Genel"}
                      </span>
                    </div>
                    <p style={styles.feedbackDescription}>{f.description}</p>
                    <p style={styles.time}>
                      {f.timestamp?.toDate?.().toLocaleString() || f.timestamp}
                    </p>
                    <div style={styles.feedbackActions}>
                      <motion.select
                        value={f.status || "pending"}
                        onChange={(e) => handleStatusChange(f.id, e.target.value)}
                        style={styles.statusSelect}
                        whileFocus={{ borderColor: "#00c4b4" }}
                        aria-label="Geri bildirim durumunu güncelle"
                      >
                        <option value="pending">Beklemede</option>
                        <option value="in_progress">İnceleniyor</option>
                        <option value="resolved">Çözüldü</option>
                      </motion.select>
                      <motion.button
                        style={styles.actionButton}
                        onClick={() => handleDeleteFeedback(f.id)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        aria-label="Geri bildirimi sil"
                      >
                        Sil
                      </motion.button>
                    </div>
                  </motion.div>
                ))
              )}
            </div>
            {filteredFeedbacks.length > displayCounts.feedback && (
              <motion.button
                style={styles.loadMoreButton}
                onClick={() => handleLoadMore("feedback")}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                aria-label="Daha fazla geri bildirim göster"
              >
                Daha Fazla Göster
              </motion.button>
            )}
          </div>
        )}

        {activeTab === "users" && (
          <div style={styles.tabContent}>
            <motion.input
              type="text"
              placeholder="Kullanıcı adı veya ID'ye göre ara..."
              value={searchQueries.users}
              onChange={(e) => handleSearchChange("users", e.target.value)}
              style={styles.searchInput}
              whileFocus={{ borderColor: "#00c4b4" }}
              aria-label="Kullanıcıları ara"
            />
            <div style={{ ...styles.tableContainer, maxHeight: '600px', overflowY: 'auto' }}>
              <table style={styles.dataTable}>
                <thead>
                  <tr>
                    <th>Kullanıcı Adı</th>
                    <th>E-posta</th>
                    <th>ID</th>
                    <th>Durum</th>
                    <th>İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan="4" style={{ textAlign: 'center', padding: '2rem' }}>Henüz kullanıcı yok.</td>
                    </tr>
                  ) : (
                    filteredUsers.map((u, index) => (
                      <motion.tr
                        key={u.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.02, duration: 0.2 }}
                      >
                        <td><strong>{u.username || "👤 İsimsiz"}</strong></td>
                        <td style={{ color: '#e2e8f0', fontSize: '0.9rem' }}>{u.email || "-"}</td>
                        <td style={{ color: '#94a3b8', fontSize: '0.85rem' }}>{u.id}</td>
                        <td>
                          {u.isPremium ? (
                            <span style={{ color: '#facc15', backgroundColor: 'rgba(234,179,8,0.1)', padding: '4px 8px', borderRadius: '4px', fontSize: '0.8rem' }}>✨ Premium</span>
                          ) : (
                            <span style={{ color: '#94a3b8', backgroundColor: 'rgba(255,255,255,0.05)', padding: '4px 8px', borderRadius: '4px', fontSize: '0.8rem' }}>Standart</span>
                          )}
                        </td>
                        <td>
                          <motion.button
                            style={{ ...styles.premiumButton, padding: '4px 8px', fontSize: '0.75rem', marginRight: '8px' }}
                            onClick={() => togglePremium(u.id, u.isPremium)}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            {u.isPremium ? 'Premium İptal' : 'Premium Yap'}
                          </motion.button>
                          <motion.button
                            style={{ ...styles.banButton, padding: '4px 8px', fontSize: '0.75rem' }}
                            onClick={() => handleBanUser(u.id)}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            Banla
                          </motion.button>
                        </td>
                      </motion.tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === "referrals" && (
          <div style={styles.tabContent}>
            <motion.input
              type="text"
              placeholder="Referans kodu, kullanıcı adı veya e-postaya göre ara..."
              value={searchQueries.referrals}
              onChange={(e) => handleSearchChange("referrals", e.target.value)}
              style={styles.searchInput}
              whileFocus={{ borderColor: "#00c4b4" }}
              aria-label="Referans kodlarını ara"
            />
            <motion.div
              style={styles.userSearchContainer}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <motion.input
                type="text"
                placeholder="Kullanıcı adı veya e-posta ile ara..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                style={styles.userSearchInput}
                whileFocus={{ borderColor: "#00c4b4" }}
                aria-label="Kullanıcıyı ara"
              />
              <motion.button
                style={styles.userSearchButton}
                onClick={handleUserSearch}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                aria-label="Kullanıcıyı bul"
              >
                Ara
              </motion.button>
            </motion.div>
            <AnimatePresence>
              {searchedUser && (
                <motion.div
                  style={styles.searchedUser}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.3 }}
                >
                  <p>
                    <strong>{searchedUser.username}</strong> ({searchedUser.email}) -{" "}
                    {searchedUser.referralCode ? (
                      <span>
                        Referans Kodu: <strong>{searchedUser.referralCode}</strong>
                      </span>
                    ) : (
                      "Referans Kodu Yok"
                    )}
                  </p>
                  <motion.button
                    style={styles.clearSearchButton}
                    onClick={() => {
                      setSearchedUser(null);
                      setUserSearch("");
                      setExpandedReferral(null);
                    }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    aria-label="Aramayı temizle"
                  >
                    Temizle
                  </motion.button>
                </motion.div>
              )}
            </AnimatePresence>
            <div style={styles.referralContainer}>
              {filteredReferrals.length === 0 && filteredNoReferralUsers.length === 0 ? (
                <p style={styles.emptyMessage}>Henüz kullanıcı kaydı yok.</p>
              ) : (
                <>
                  {filteredReferrals.slice(0, displayCounts.referrals).map((referral, index) => (
                    <motion.div
                      key={referral.referralCode}
                      style={styles.referralList}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05, duration: 0.3 }}
                    >
                      <motion.div
                        style={styles.referralHeader}
                        onClick={() =>
                          setExpandedReferral(
                            expandedReferral === referral.referralCode ? null : referral.referralCode
                          )
                        }
                        whileHover={{ backgroundColor: "rgba(255, 255, 255, 0.05)" }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === "Enter" && setExpandedReferral(referral.referralCode)}
                        aria-expanded={expandedReferral === referral.referralCode}
                        aria-label={`Referans kodu ${referral.referralCode} detaylarını göster`}
                      >
                        <h3 style={styles.referralCodeTitle}>
                          Referans Kodu: {referral.referralCode}
                        </h3>
                        <p style={styles.referralCount}>
                          Kayıt Olan Kullanıcı Sayısı: {referral.users.length}
                        </p>
                        <motion.div
                          style={styles.expandIcon}
                          animate={{ rotate: expandedReferral === referral.referralCode ? 180 : 0 }}
                        >
                          ▼
                        </motion.div>
                      </motion.div>
                      <AnimatePresence>
                        {expandedReferral === referral.referralCode && (
                          <motion.div
                            style={styles.referralTableContainer}
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                          >
                            <table style={styles.referralTable}>
                              <thead>
                                <tr>
                                  <th>Kullanıcı Adı</th>
                                  <th>E-posta</th>
                                  <th>Premium Durumu</th>
                                </tr>
                              </thead>
                              <tbody>
                                {referral.users.map((user, i) => (
                                  <motion.tr
                                    key={i}
                                    style={styles.referralUserRow}
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: i * 0.05, duration: 0.2 }}
                                    whileHover={{ backgroundColor: "rgba(255, 255, 255, 0.05)" }}
                                  >
                                    <td>{user.username}</td>
                                    <td>{user.email}</td>
                                    <td>{user.isPremium ? "✅ Evet" : "❌ Hayır"}</td>
                                  </motion.tr>
                                ))}
                              </tbody>
                            </table>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  ))}
                  {filteredNoReferralUsers.length > 0 && (
                    <motion.div
                      style={styles.referralList}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: filteredReferrals.length * 0.05, duration: 0.3 }}
                    >
                      <motion.div
                        style={styles.referralHeader}
                        onClick={() =>
                          setExpandedReferral(
                            expandedReferral === "no-referral" ? null : "no-referral"
                          )
                        }
                        whileHover={{ backgroundColor: "rgba(255, 255, 255, 0.05)" }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === "Enter" && setExpandedReferral("no-referral")}
                        aria-expanded={expandedReferral === "no-referral"}
                        aria-label="Referans kodu olmayan kullanıcıları göster"
                      >
                        <h3 style={styles.referralCodeTitle}>Referans Kodu Olmayanlar</h3>
                        <p style={styles.referralCount}>
                          Kullanıcı Sayısı: {filteredNoReferralUsers.length}
                        </p>
                        <motion.div
                          style={styles.expandIcon}
                          animate={{ rotate: expandedReferral === "no-referral" ? 180 : 0 }}
                        >
                          ▼
                        </motion.div>
                      </motion.div>
                      <AnimatePresence>
                        {expandedReferral === "no-referral" && (
                          <motion.div
                            style={styles.referralTableContainer}
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                          >
                            <table style={styles.referralTable}>
                              <thead>
                                <tr>
                                  <th>Kullanıcı Adı</th>
                                  <th>E-posta</th>
                                  <th>Premium Durumu</th>
                                </tr>
                              </thead>
                              <tbody>
                                {filteredNoReferralUsers.map((user, i) => (
                                  <motion.tr
                                    key={i}
                                    style={styles.referralUserRow}
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: i * 0.05, duration: 0.2 }}
                                    whileHover={{ backgroundColor: "rgba(255, 255, 255, 0.05)" }}
                                  >
                                    <td>{user.username}</td>
                                    <td>{user.email}</td>
                                    <td>{user.isPremium ? "✅ Evet" : "❌ Hayır"}</td>
                                  </motion.tr>
                                ))}
                              </tbody>
                            </table>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  )}
                </>
              )}
            </div>
            {(filteredReferrals.length > displayCounts.referrals ||
              filteredNoReferralUsers.length > displayCounts.referrals) && (
              <motion.button
                style={styles.loadMoreButton}
                onClick={() => handleLoadMore("referrals")}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                aria-label="Daha fazla referans kodu göster"
              >
                Daha Fazla Göster
              </motion.button>
            )}
          </div>
        )}

        {activeTab === "coaching" && (
          <div style={styles.tabContent}>
            {/* Make User Coach */}
            <div style={{ ...styles.card, marginBottom: '20px' }}>
              <h3 style={{ marginBottom: '12px', color: '#10b981' }}>🎓 Kullanıcıyı Koç Yap</h3>
              <p style={{ fontSize: '13px', color: '#999', marginBottom: '12px' }}>Bir kullanıcının ID'sini girerek onu koç rolüne yükseltebilirsiniz.</p>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  placeholder="Kullanıcı ID veya Kullanıcı Adı"
                  id="coachRoleInput"
                  style={styles.searchInput}
                />
                <motion.button
                  style={{ ...styles.actionButton, background: '#10b981' }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={async () => {
                    const input = document.getElementById('coachRoleInput');
                    const rawUid = input?.value?.trim();
                    const uid = resolveUserId(rawUid);
                    if (!uid) { toast.error('ID veya Kullanıcı adı gerekli'); return; }
                    try {
                      await setUserRole(uid, 'coach');
                      toast.success(`${uid} artık koç!`);
                      input.value = '';
                      fetchUsers();
                    } catch (err) { toast.error('Rol güncellenemedi: ' + err.message); }
                  }}
                >Koç Yap</motion.button>
                <motion.button
                  style={{ ...styles.actionButton, background: '#6366f1' }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={async () => {
                    const input = document.getElementById('coachRoleInput');
                    const rawUid = input?.value?.trim();
                    const uid = resolveUserId(rawUid);
                    if (!uid) { toast.error('ID veya Kullanıcı adı gerekli'); return; }
                    try {
                      await setUserRole(uid, 'student');
                      toast.success(`${uid} artık öğrenci!`);
                      input.value = '';
                      fetchUsers();
                    } catch (err) { toast.error('Rol güncellenemedi: ' + err.message); }
                  }}
                >Öğrenci Yap</motion.button>
              </div>
            </div>

            {/* Assign Student to Coach */}
            <div style={{ ...styles.card, marginBottom: '20px' }}>
              <h3 style={{ marginBottom: '12px', color: '#818cf8' }}>🔗 Öğrenciyi Koça Ata</h3>
              <p style={{ fontSize: '13px', color: '#999', marginBottom: '12px' }}>Öğrenci ID ve Koç ID girerek eşleştirme yapabilirsiniz.</p>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  placeholder="Öğrenci ID veya Kullanıcı Adı"
                  value={coachAssignStudentId}
                  onChange={e => setCoachAssignStudentId(e.target.value)}
                  style={{ ...styles.searchInput, maxWidth: '200px' }}
                />
                <input
                  type="text"
                  placeholder="Koç ID veya Kullanıcı Adı"
                  value={coachAssignCoachId}
                  onChange={e => setCoachAssignCoachId(e.target.value)}
                  style={{ ...styles.searchInput, maxWidth: '200px' }}
                />
                <motion.button
                  style={{ ...styles.actionButton, background: '#10b981' }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={async () => {
                    if (!coachAssignStudentId.trim() || !coachAssignCoachId.trim()) {
                      toast.error('Her iki bilgi de gerekli'); return;
                    }
                    const sId = resolveUserId(coachAssignStudentId);
                    const cId = resolveUserId(coachAssignCoachId);
                    try {
                      await assignStudentToCoach(sId, cId);
                      toast.success('Öğrenci koça atandı!');
                      setCoachAssignStudentId('');
                      setCoachAssignCoachId('');
                    } catch (err) { toast.error('Atama yapılamadı: ' + err.message); }
                  }}
                >Ata</motion.button>
                <motion.button
                  style={{ ...styles.actionButton, background: '#ef4444' }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={async () => {
                    if (!coachAssignStudentId.trim()) {
                      toast.error('Öğrenci ID gerekli'); return;
                    }
                    const sId = resolveUserId(coachAssignStudentId);
                    if (!sId) {
                      toast.error('Öğrenci bulunamadı'); return;
                    }
                    try {
                      await removeStudentFromCoach(sId);
                      toast.success('Öğrenci koçtan ayrıldı!');
                      setCoachAssignStudentId('');
                    } catch (err) { toast.error('İşlem yapılamadı: ' + err.message); }
                  }}
                >Koçtan Ayır</motion.button>
              </div>
            </div>

            {/* Coaches List */}
            <div style={{ ...styles.card }}>
              <h3 style={{ marginBottom: '12px', color: '#f59e0b' }}>📋 Mevcut Koçlar</h3>
              <div style={styles.cardGrid}>
                {users.filter(u => u.role === 'coach').length === 0 ? (
                  <p style={styles.emptyMessage}>Henüz koç atanmadı.</p>
                ) : (
                  users.filter(u => u.role === 'coach').map((coach, index) => (
                    <motion.div
                      key={coach.id}
                      style={styles.card}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <p><strong>🎓 {coach.username || 'İsimsiz'}</strong></p>
                      <p style={{ fontSize: '12px', color: '#888' }}>ID: {coach.id}</p>
                      <p style={{ fontSize: '12px', color: '#888' }}>E-posta: {coach.email || '-'}</p>
                      <p style={{ fontSize: '12px', color: '#10b981', marginTop: '4px' }}>
                        Öğrenci sayısı: {users.filter(u => u.coachId === coach.id).length}
                      </p>
                    </motion.div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === "broadcast" && (
          <div style={styles.tabContent}>
            <div style={{ ...styles.card, marginBottom: '20px', border: '1px solid rgba(0, 230, 255, 0.5)' }}>
              <h3 style={{ marginBottom: '16px', color: '#00e6ff', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '24px' }}>📢</span> Global Duyuru Gönder
              </h3>
              <p style={{ fontSize: '14px', color: '#aaa', marginBottom: '20px', lineHeight: '1.5' }}>
                Buradan göndereceğiniz mesaj, platformdaki <strong>tüm kullanıcıların bildirim paneline</strong> anında düşecektir.
                Önemli sistem duyuruları veya kampanya bildirimleri için kullanabilirsiniz.
              </p>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: '#00e6ff', fontSize: '14px' }}>Duyuru Başlığı</label>
                  <input
                    type="text"
                    placeholder="Örn: Sistem Güncellemesi"
                    value={broadcastTitle}
                    onChange={e => setBroadcastTitle(e.target.value)}
                    style={{ ...styles.searchInput, maxWidth: '100%' }}
                  />
                </div>
                
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: '#00e6ff', fontSize: '14px' }}>Duyuru Mesajı</label>
                  <textarea
                    placeholder="Göndermek istediğiniz mesajı buraya yazın..."
                    value={broadcastMessage}
                    onChange={e => setBroadcastMessage(e.target.value)}
                    style={{ 
                      ...styles.searchInput, 
                      maxWidth: '100%', 
                      minHeight: '120px', 
                      resize: 'vertical',
                      fontFamily: 'inherit'
                    }}
                  />
                </div>

                <motion.button
                  style={{ 
                    ...styles.actionButton, 
                    background: 'linear-gradient(135deg, rgba(0, 230, 255, 0.4), rgba(0, 230, 255, 0.1))',
                    padding: '12px 24px',
                    fontSize: '16px',
                    marginTop: '10px',
                    alignSelf: 'flex-start',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    opacity: isBroadcasting ? 0.7 : 1,
                    cursor: isBroadcasting ? 'not-allowed' : 'pointer'
                  }}
                  whileHover={!isBroadcasting ? { scale: 1.05 } : {}}
                  whileTap={!isBroadcasting ? { scale: 0.95 } : {}}
                  onClick={handleSendBroadcast}
                  disabled={isBroadcasting}
                >
                  {isBroadcasting ? '⏳ Gönderiliyor...' : '🚀 Tüm Kullanıcılara Gönder'}
                </motion.button>
              </div>
            </div>
          </div>
        )}
      </motion.main>
      </div>
    </div>
  );
}

const styles = {
  layoutWrapper: {
    display: "flex",
    minHeight: "100vh",
    background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
    color: "#f8fafc",
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
  },
  sidebar: {
    width: "280px",
    background: "rgba(15, 23, 42, 0.8)",
    backdropFilter: "blur(20px)",
    borderRight: "1px solid rgba(255, 255, 255, 0.05)",
    display: "flex",
    flexDirection: "column",
    position: "fixed",
    height: "100vh",
    top: 0,
    left: 0,
    zIndex: 50,
  },
  sidebarHeader: {
    padding: "2rem 1.5rem",
    borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
    marginBottom: "1rem"
  },
  sidebarNav: {
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem",
    padding: "0 1rem",
    flex: 1,
    overflowY: "auto"
  },
  sidebarItem: {
    padding: "0.875rem 1rem",
    background: "transparent",
    border: "none",
    borderRadius: "8px",
    color: "#94a3b8",
    textAlign: "left",
    cursor: "pointer",
    fontSize: "0.95rem",
    fontWeight: "500",
    transition: "all 0.2s ease",
  },
  sidebarItemActive: {
    padding: "0.875rem 1rem",
    background: "rgba(99, 102, 241, 0.15)",
    border: "1px solid rgba(99, 102, 241, 0.2)",
    borderRadius: "8px",
    color: "#e0e7ff",
    textAlign: "left",
    cursor: "pointer",
    fontSize: "0.95rem",
    fontWeight: "600",
    transition: "all 0.2s ease",
  },
  sidebarBackButton: {
    width: "100%",
    padding: "0.875rem",
    background: "rgba(255, 255, 255, 0.03)",
    border: "1px solid rgba(255, 255, 255, 0.05)",
    borderRadius: "8px",
    color: "#cbd5e1",
    cursor: "pointer",
    fontSize: "0.9rem",
    fontWeight: "500",
    transition: "all 0.2s ease",
  },
  mainContent: {
    flex: 1,
    marginLeft: "280px",
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
  },
  topBar: {
    padding: "2rem",
    borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
    background: "rgba(15, 23, 42, 0.4)",
    backdropFilter: "blur(10px)",
    position: "sticky",
    top: 0,
    zIndex: 40,
  },
  topBarTitle: {
    fontSize: "1.75rem",
    fontWeight: "700",
    color: "#f8fafc",
    margin: 0,
    letterSpacing: "-0.5px"
  },
  container: {
    padding: "2rem",
    paddingTop: "env(safe-area-inset-top)",
    paddingBottom: "env(safe-area-inset-bottom)",
    background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
    minHeight: "100vh",
    color: "#f8fafc",
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
    position: "relative",
    overflowX: "hidden"
  },
  panelHeader: {
    textAlign: "center",
    marginBottom: "2rem",
    position: "relative"
  },
  panelTitle: {
    fontSize: "2.25rem",
    margin: 0,
    color: "#ffffff",
    fontWeight: "800",
    letterSpacing: "-0.5px",
    textShadow: "0 4px 20px rgba(0, 0, 0, 0.2)",
  },
  fullscreenLoading: {
    height: "100vh",
    width: "100vw",
    background: "#0f172a",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#6366f1",
    fontFamily: "'Inter', sans-serif",
    fontSize: "1.25rem",
    fontWeight: "600"
  },
  glitchText: {
    letterSpacing: "2px",
    fontWeight: "600",
    textTransform: "uppercase"
  },
  fullscreenDenied: {
    height: "100vh",
    width: "100vw",
    background: "linear-gradient(135deg, #450a0a 0%, #0f172a 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#ef4444",
    fontFamily: "'Inter', sans-serif",
  },
  deniedTitle: {
    fontSize: "3rem",
    fontWeight: "800",
    margin: 0,
    letterSpacing: "-1px",
    color: "#fca5a5"
  },
  deniedSub: {
    fontSize: "1.125rem",
    color: "#f87171",
    marginTop: "1rem",
    marginBottom: "2rem"
  },
  glitchButton: {
    padding: "0.75rem 1.5rem",
    background: "rgba(239, 68, 68, 0.1)",
    border: "1px solid rgba(239, 68, 68, 0.5)",
    color: "#ef4444",
    fontWeight: "600",
    fontSize: "1rem",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "all 0.2s ease"
  },
  statsSection: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "1.5rem",
  },
  statValue: {
    fontSize: "2.5rem",
    fontWeight: "800",
    color: "#818cf8",
    margin: "0.5rem 0 0 0",
    lineHeight: "1"
  },
  statCard: {
    background: "rgba(255, 255, 255, 0.03)",
    backdropFilter: "blur(12px)",
    padding: "1.5rem 2rem",
    borderRadius: "16px",
    minWidth: "220px",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    border: "1px solid rgba(255, 255, 255, 0.05)",
  },
  tabNav: {
    display: "flex",
    justifyContent: "center",
    gap: "0.75rem",
    marginBottom: "2rem",
    flexWrap: "wrap",
  },
  tabButton: {
    padding: "0.75rem 1.25rem",
    background: "rgba(255, 255, 255, 0.03)",
    backdropFilter: "blur(8px)",
    border: "1px solid rgba(255, 255, 255, 0.05)",
    borderRadius: "10px",
    color: "#94a3b8",
    cursor: "pointer",
    fontSize: "0.95rem",
    fontWeight: "500",
    transition: "all 0.2s ease",
  },
  activeTab: {
    padding: "0.75rem 1.25rem",
    background: "rgba(99, 102, 241, 0.15)",
    backdropFilter: "blur(8px)",
    border: "1px solid rgba(99, 102, 241, 0.4)",
    borderRadius: "10px",
    color: "#e0e7ff",
    cursor: "pointer",
    fontSize: "0.95rem",
    fontWeight: "600",
    transition: "all 0.2s ease",
  },
  contentArea: {
    padding: "2rem",
    maxWidth: "1200px",
    width: "100%",
    margin: "0 auto"
  },
  tabContent: {
    display: "flex",
    flexDirection: "column",
    gap: "2rem",
  },
  tableContainer: {
    width: "100%",
    overflowX: "auto",
    background: "rgba(255, 255, 255, 0.02)",
    borderRadius: "12px",
    border: "1px solid rgba(255, 255, 255, 0.05)",
  },
  dataTable: {
    width: "100%",
    borderCollapse: "collapse",
    textAlign: "left",
  },
  searchInput: {
    padding: "0.875rem 1.25rem",
    width: "100%",
    maxWidth: "500px",
    borderRadius: "10px",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    background: "rgba(255, 255, 255, 0.03)",
    color: "#f8fafc",
    fontSize: "0.95rem",
    outline: "none",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
    transition: "border-color 0.2s ease",
  },
  cardGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: "1.25rem",
  },
  card: {
    background: "rgba(255, 255, 255, 0.03)",
    backdropFilter: "blur(12px)",
    padding: "1.5rem",
    borderRadius: "16px",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    border: "1px solid rgba(255, 255, 255, 0.05)",
    transition: "all 0.2s ease"
  },
  time: {
    fontSize: "0.85rem",
    color: "#94a3b8",
    marginBottom: "1rem",
  },
  actionButton: {
    padding: "0.5rem 1rem",
    background: "rgba(99, 102, 241, 0.1)",
    color: "#818cf8",
    border: "1px solid rgba(99, 102, 241, 0.3)",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "0.875rem",
    fontWeight: "600",
    marginRight: "0.5rem",
    transition: "all 0.2s ease",
  },
  banButton: {
    padding: "0.5rem 1rem",
    background: "rgba(239, 68, 68, 0.1)",
    color: "#ef4444",
    border: "1px solid rgba(239, 68, 68, 0.3)",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "0.875rem",
    fontWeight: "600",
    transition: "all 0.2s ease",
  },
  commentsSection: {
    marginTop: "1rem",
    background: "rgba(0, 0, 0, 0.15)",
    padding: "1rem",
    borderRadius: "12px",
    border: "1px solid rgba(255, 255, 255, 0.05)"
  },
  commentCard: {
    background: "rgba(255, 255, 255, 0.02)",
    padding: "0.75rem",
    borderRadius: "8px",
    marginTop: "0.75rem",
    display: "flex",
    flexDirection: "column",
    gap: "0.25rem",
  },
  commentTime: {
    fontSize: "0.75rem",
    color: "#94a3b8",
  },
  smallActionButton: {
    padding: "0.25rem 0.5rem",
    background: "rgba(239, 68, 68, 0.1)",
    color: "#ef4444",
    border: "1px solid rgba(239, 68, 68, 0.2)",
    borderRadius: "6px",
    fontSize: "0.75rem",
    cursor: "pointer",
    alignSelf: "flex-end",
  },
  feedbackHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "0.75rem",
  },
  feedbackTitle: {
    fontSize: "1.1rem",
    color: "#f8fafc",
    margin: 0,
    fontWeight: "600",
  },
  feedbackType: {
    fontSize: "0.75rem",
    color: "#818cf8",
    background: "rgba(99, 102, 241, 0.1)",
    padding: "0.25rem 0.75rem",
    borderRadius: "999px",
    fontWeight: "500",
    textTransform: "uppercase"
  },
  feedbackDescription: {
    fontSize: "0.95rem",
    color: "#cbd5e1",
    marginBottom: "1rem",
    lineHeight: "1.5"
  },
  feedbackActions: {
    display: "flex",
    gap: "0.75rem",
    alignItems: "center",
  },
  statusSelect: {
    padding: "0.6rem",
    borderRadius: "8px",
    border: "1px solid rgba(0, 230, 255, 0.4)",
    background: "rgba(10, 15, 30, 0.8)",
    color: "#00e6ff",
    fontSize: "0.875rem",
    cursor: "pointer",
    outline: "none"
  },
  emptyMessage: {
    textAlign: "center",
    color: "#888",
    fontSize: "1.1rem",
    margin: "2rem 0",
    fontFamily: "monospace"
  },
  errorMessage: {
    color: "#ef4444",
    textAlign: "center",
    fontSize: "1.1rem",
    marginBottom: "1.5rem",
    background: "rgba(239, 68, 68, 0.1)",
    padding: "1rem",
    borderRadius: "10px",
    border: "1px solid rgba(239, 68, 68, 0.2)"
  },
  userSearchContainer: {
    display: "flex",
    gap: "0.75rem",
    marginBottom: "1.5rem",
    justifyContent: "center",
    flexWrap: "wrap",
  },
  userSearchInput: {
    padding: "0.875rem 1.25rem",
    width: "100%",
    maxWidth: "350px",
    borderRadius: "10px",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    background: "rgba(255, 255, 255, 0.03)",
    color: "#f8fafc",
    fontSize: "0.95rem",
    outline: "none",
  },
  userSearchButton: {
    padding: "0.875rem 1.5rem",
    background: "rgba(99, 102, 241, 0.15)",
    color: "#818cf8",
    border: "1px solid rgba(99, 102, 241, 0.4)",
    borderRadius: "10px",
    cursor: "pointer",
    fontSize: "0.95rem",
    fontWeight: "600",
    transition: "all 0.2s ease"
  },
  clearSearchButton: {
    padding: "0.5rem 1rem",
    background: "rgba(239, 68, 68, 0.1)",
    color: "#ef4444",
    border: "1px solid rgba(239, 68, 68, 0.2)",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "0.875rem",
    fontWeight: "500",
  },
  searchedUser: {
    background: "rgba(255, 255, 255, 0.03)",
    padding: "1.25rem",
    borderRadius: "12px",
    marginBottom: "1.5rem",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "1rem",
    border: "1px solid rgba(255, 255, 255, 0.05)",
  },
  referralContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
  },
  referralList: {
    background: "rgba(255, 255, 255, 0.03)",
    backdropFilter: "blur(12px)",
    padding: "1.5rem",
    borderRadius: "16px",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
    border: "1px solid rgba(255, 255, 255, 0.05)",
  },
  referralHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "1rem",
    cursor: "pointer",
    borderRadius: "12px",
    background: "rgba(255, 255, 255, 0.02)",
    border: "1px solid rgba(255, 255, 255, 0.05)"
  },
  referralCodeTitle: {
    fontSize: "1.1rem",
    color: "#f8fafc",
    margin: 0,
    fontWeight: "600",
  },
  referralCount: {
    fontSize: "0.9rem",
    color: "#94a3b8",
    margin: 0,
  },
  expandIcon: {
    fontSize: "1.2rem",
    color: "#94a3b8",
  },
  referralTableContainer: {
    overflow: "hidden",
    marginTop: "1rem",
  },
  referralTable: {
    width: "100%",
    borderCollapse: "collapse",
    color: "#e2e8f0",
  },
  referralUserRow: {
    borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
  },
  loadMoreButton: {
    padding: "0.875rem 1.75rem",
    background: "rgba(255, 255, 255, 0.03)",
    color: "#f8fafc",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    borderRadius: "10px",
    cursor: "pointer",
    fontSize: "0.95rem",
    fontWeight: "500",
    alignSelf: "center",
    marginTop: "2rem",
    transition: "all 0.2s ease"
  },
  premiumButton: {
    padding: "0.5rem 1rem",
    background: "rgba(234, 179, 8, 0.1)",
    color: "#facc15",
    border: "1px solid rgba(234, 179, 8, 0.3)",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "0.875rem",
    fontWeight: "600",
    marginRight: "0.5rem",
    transition: "all 0.2s ease",
  }
};

styles.dataTable["th"] = {
  background: "rgba(255, 255, 255, 0.02)",
  padding: "1rem",
  textAlign: "left",
  fontSize: "0.875rem",
  color: "#94a3b8",
  borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
  fontWeight: "500"
};

styles.dataTable["td"] = {
  padding: "1rem",
  fontSize: "0.95rem",
  color: "#cbd5e1",
  borderBottom: "1px solid rgba(255, 255, 255, 0.02)",
};

styles.referralTable["th"] = styles.dataTable["th"];
styles.referralTable["td"] = styles.dataTable["td"];

// Responsive styles
const responsiveStyles = `
  @media (max-width: 1024px) {
    .sidebar {
      width: 220px;
    }
    .mainContent {
      margin-left: 220px;
    }
    .cardGrid {
      grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    }
  }

  @media (max-width: 768px) {
    .layoutWrapper {
      flex-direction: column !important;
    }
    .sidebar {
      width: 100% !important;
      height: auto !important;
      position: relative !important;
      border-right: none !important;
      border-bottom: 1px solid rgba(255, 255, 255, 0.05);
      z-index: 10;
    }
    .sidebarNav {
      flex-direction: row !important;
      flex-wrap: wrap;
      overflow-x: auto;
      padding: 1rem !important;
    }
    .mainContent {
      margin-left: 0 !important;
    }
    .topBar {
      padding: 1.5rem 1rem !important;
    }
    .contentArea {
      padding: 1rem !important;
    }
    .panelTitle {
      font-size: 1.8rem;
    }
    .statCard {
      min-width: 150px;
      padding: 1.5rem;
    }
    .userSearchInput {
      max-width: 100%;
    }
    .card {
      padding: 1.2rem;
    }
  }

  @media (max-width: 480px) {
    .topBarTitle {
      font-size: 1.5rem;
    }
    .statCard {
      min-width: 100%;
    }
    .cardGrid {
      grid-template-columns: 1fr;
    }
    .actionButton, .banButton, .loadMoreButton, .premiumButton {
      padding: 0.6rem 1rem;
      font-size: 0.8rem;
      margin-bottom: 0.5rem;
    }
  }
`;

// Inject responsive styles
const styleSheet = document.createElement("style");
styleSheet.textContent = responsiveStyles;
document.head.appendChild(styleSheet);

export default AdminPanel;